import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { QrCode, CheckCircle2, Loader2 } from 'lucide-react';
import { useReceiveEcash, type ReceiveState } from '@/hooks/useReceiveEcash';
import { formatXmr } from '@/lib/monero/units';
import { useToast } from '@/hooks/useToast';
import { getTokenMetadata } from '@/lib/cashu/token';
import type { WalletMintEntry } from '@/lib/storage/db';

interface ReceiveEcashDialogProps {
  mint: WalletMintEntry;
  userPrivkey: string;
}

export function ReceiveEcashDialog({ mint, userPrivkey }: ReceiveEcashDialogProps) {
  const [open, setOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const { state, receiveToken, reset } = useReceiveEcash();
  const { toast } = useToast();

  const handleReceive = async () => {
    if (!tokenInput.trim()) {
      toast({ title: 'Paste a token first', variant: 'destructive' });
      return;
    }
    if (!tokenInput.trim().startsWith('casheroA')) {
      toast({ title: 'Invalid token', description: 'Token must start with "casheroA"', variant: 'destructive' });
      return;
    }
    await receiveToken(tokenInput.trim(), mint.keysets, mint.npub, userPrivkey);
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) { reset(); setTokenInput(''); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <QrCode className="h-4 w-4 mr-1" />
          Receive
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Receive Ecash</DialogTitle>
          <DialogDescription>
            Paste an ecash token to claim it. Tokens are swapped at the mint for fresh proofs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {(state.step === 'idle' || state.step === 'error') && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Paste Ecash Token</label>
                <Textarea
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="casheroA..."
                  className="text-xs font-mono h-24 resize-none"
                />
              </div>
              {tokenInput.trim().startsWith('casheroA') && (
                (() => {
                  try {
                    const meta = getTokenMetadata(tokenInput.trim());
                    return (
                      <div className="p-3 bg-muted rounded-lg text-xs space-y-1">
                        <p><strong>Amount:</strong> {formatXmr(meta.amount, 6)} XMR</p>
                        <p><strong>Mint:</strong> {meta.mint.slice(0, 12)}...</p>
                        {meta.memo && <p><strong>Memo:</strong> {meta.memo}</p>}
                      </div>
                    );
                  } catch {
                    return <p className="text-xs text-destructive">Invalid token format</p>;
                  }
                })()
              )}
              <Button onClick={handleReceive} className="w-full" disabled={!tokenInput.trim()}>
                <QrCode className="h-4 w-4 mr-2" />
                Receive
              </Button>
              {state.step === 'error' && (
                <p className="text-xs text-destructive">{state.error}</p>
              )}
            </>
          )}

          {(state.step === 'decoding' || state.step === 'swapping') && (
            <div className="flex items-center justify-center py-8 flex-col gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {state.step === 'decoding' ? 'Decoding token...' : 'Swapping at mint for fresh proofs...'}
              </p>
            </div>
          )}

          {state.step === 'done' && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
              <p className="font-semibold text-lg">{formatXmr(state.amount, 6)} XMR received!</p>
              <p className="text-sm text-muted-foreground">
                Fresh ecash proofs have been added to your wallet.
              </p>
              <Button onClick={() => { reset(); setTokenInput(''); }} variant="outline" className="w-full">
                Receive Another
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
